// installPreviewAiTransport(el) — wires the preview AI panel (the shared simone
// Chat component) to dispatch through sdk.ai, mirroring the bridge protocol the
// Chat surface expects. Adapted from workspace/functions/simoneAiTransport.js,
// slimmed to the preview surface: workspace-scoped (no /add-app, no local
// ws-bridge), and — the preview-specific bit — it targets the PREVIEWED project
// so the assistant's server-side `commit_project_changes` edits THAT project,
// then re-renders the iframe to reflect any committed changes.
//
// sendPrompt snapshots the user message into root.chatMessages, flips
// chatTyping:true, and calls window.__simoneSendOverBridge(envelope, { stateRoot }).
// We own that handle and route it to sdk.ai.dispatch.

// Bare shared-library import, not a deep functions/aiService.js subpath — a
// per-file deep specifier into @symbo-ls/workspace-shared cannot resolve
// through the platform's public importmap (tickets/execution-board.md §1,
// same fix as workspace/app.js — this file rides the workspace-shell EAGER
// chunk via preview/functions/eagerSlice.js, so it is a reached path).
// Deferred call wrapper, not a module-scope destructure, per the a5d0a2aa
// barrel-cycle lesson.
import _wsShared from '@symbo-ls/workspace-shared'
const previewGetAiService = (...args) => _wsShared.functions.getAiService(...args)

let _previewInstalled = false

// Resolve the LIVE root. The auth/shell boot can remount the tree, so a root
// captured at install goes stale (patches no-op → empty bubbles). Prefer the
// root the sender passed alongside the envelope (resolved fresh at send time).
// The installer element handle lives on `globalThis` rather than a
// module-scope `let` — frank's globalScope-lift promotes `installPreviewAiTransport`
// / `_previewResolveRoot` into independently-serialized functions (referenced as
// string handlers elsewhere), which strips the shared module closure; a
// plain `let` free var then revives as a guaranteed `ReferenceError`. A
// `globalThis` property read/write needs no closure — it resolves the same
// inside every independently-serialized function. See tickets/opus.md
// "canvas serialization defects blocking the authorised production push".
const _previewResolveRoot = (extra) => {
  if (extra && extra.stateRoot && typeof extra.stateRoot.update === 'function') {
    return extra.stateRoot
  }
  const el = globalThis.__previewAiInstallerEl
  return (
    (el && el.state && el.state.root) ||
    (el && typeof el.getRoot === 'function' && el.getRoot()?.state?.root) ||
    null
  )
}

// Same precedence as workspace scopedClient.activeWorkspaceId, plus the previewed
// project's own workspace and the localStorage fallback sdk.ai itself reads
// (preview.* is a separate origin with no session activeWorkspace, so the
// project's workspace — pinned by bootPreviewAssistant — is the reliable source).
const _previewActiveWorkspaceId = (root) => {
  const fromState =
    (root && root.workspace && root.workspace.id) ||
    root?.jwtClaims?.active_workspace_id ||
    root?._previewProject?.workspaceId
  if (fromState) return fromState
  try {
    if (typeof window !== 'undefined') return window.localStorage.getItem('activeWorkspace') || null
  } catch (err) {
    console.warn('[preview] read activeWorkspace:', err)
  }
  return null
}

const _previewPatchAssistantMessage = (root, id, patch) => {
  if (!root?.update) return
  const list = root.chatMessages || []
  root.update({ chatMessages: list.map((m) => (m.id === id ? { ...m, ...patch } : m)) })
}

const _previewPushSystemMessage = (root, text) => {
  if (!root?.update) return
  root.update({
    chatMessages: (root.chatMessages || []).concat({
      id: `sys-${Date.now()}`,
      role: 'system',
      text,
      ts: Date.now()
    })
  })
}

export function installPreviewAiTransport(el) {
  if (typeof window === 'undefined') return
  // Refresh the handle on every call so re-installs (remounts) point at the
  // current tree; the window handler installs once.
  globalThis.__previewAiInstallerEl = el
  const initRoot = _previewResolveRoot(null)
  // simone's composer disables Send while bridgeStatus !== 'open'; the AI
  // service does the real round-trip, so we look healthy once installed.
  if (initRoot?.update) initRoot.update({ bridgeStatus: 'open', agent: 'sdk-ai' })
  if (_previewInstalled) return
  _previewInstalled = true

  window.__simoneSendOverBridge = (envelope, extra) => {
    if (!envelope || envelope.type !== 'user_message') return
    const root = _previewResolveRoot(extra)
    const text = String(envelope.text || '').trim()
    if (!text) return

    const ai = previewGetAiService()
    if (!ai) {
      _previewPushSystemMessage(root, 'AI service not ready — please retry in a moment.')
      if (root?.update) root.update({ chatTyping: false })
      return
    }

    // Route through the DEFAULT (workspace-scoped) Simone AI endpoint — the same
    // backend the workspace assistant uses. We deliberately do NOT pass a
    // projectId: the project-scoped conversation endpoint returned no assistant
    // response. The previewed project rides along in `context` instead, so the
    // agent's commit_project_changes still targets it. The active workspace is
    // the project's own workspace, pinned into localStorage 'activeWorkspace' by
    // bootPreviewAssistant (which sdk.ai reads via _readActiveWorkspace).
    const proj = (root && root._previewProject) || null
    const wsId = _previewActiveWorkspaceId(root)
    if (!wsId) {
      _previewPushSystemMessage(
        root,
        'Sign in and open this project in your workspace to use the assistant here.'
      )
      if (root?.update) root.update({ chatTyping: false })
      return
    }

    const assistantId = `a-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`
    if (root?.update) {
      root.update({
        chatMessages: (root.chatMessages || []).concat({
          id: assistantId,
          role: 'assistant',
          text: '',
          ts: Date.now(),
          streaming: true
        }),
        currentAssistantId: assistantId
      })
    }

    // Picked elements are POINTERS: the user aimed at something in the preview to
    // talk about / edit it. Compact one-liners prefixed to the dispatched text so
    // the agent always sees them even if it ignores structured context.
    const picked = Array.isArray(envelope.context?.pickedElements)
      ? envelope.context.pickedElements
      : []
    const pointing = picked.length
      ? picked
          .map((p) => {
            const path = (p?.keyPath || []).join(' › ')
            const name = path || p?.component || p?.key || p?.tag || 'element'
            const snippet = (p?.text || '').trim().slice(0, 80)
            return `[user is pointing at: ${name}${snippet ? ` — "${snippet}"` : ''}]`
          })
          .join('\n') + '\n\n'
      : ''

    const dispatchContext = {
      surface: 'preview',
      // Which project the user is looking at — so the agent edits the right one
      // even though the conversation is workspace-scoped (the default endpoint).
      previewProject: proj
        ? {
            id: proj.id,
            owner: proj.owner,
            key: proj.key,
            env: proj.env,
            workspaceId: proj.workspaceId
          }
        : null,
      pickedElements: picked.length ? picked : null
    }

    // Continue the active thread when its id is a real server id.
    const activeConvId = (() => {
      const id = root.activeSessionId
      return id && !String(id).startsWith('s-') ? id : null
    })()

    let buffered = ''
    ai.dispatch(
      { text: pointing + text, conversationId: activeConvId, context: dispatchContext },
      {
        onChunk: (delta) => {
          buffered += delta
          _previewPatchAssistantMessage(root, assistantId, { text: buffered })
        },
        onDone: (result) => {
          const finalText = result?.text || buffered || ''
          _previewPatchAssistantMessage(root, assistantId, { text: finalText, streaming: false })
          if (root?.update) {
            const patch = { chatTyping: false, currentAssistantId: null }
            if (result?.conversationId) patch.activeSessionId = result.conversationId
            root.update(patch)
          }
          // Reflect any server-side project edits the agent committed this turn:
          // re-fetch the current env's data and re-render the iframe if it changed.
          // Best-effort — chat stays usable even if the refresh fails.
          try {
            const callEl = globalThis.__previewAiInstallerEl
            if (callEl && typeof callEl.call === 'function') callEl.call('reloadPreviewProject')
          } catch (err) {
            console.warn('[preview] post-turn project reload:', err)
          }
        },
        onError: (err) => {
          const msg = err?.message || String(err)
          const el = globalThis.__previewAiInstallerEl
          const template =
            el && typeof el.call === 'function'
              ? el.call('polyglot', 'preview.assistant.requestFailed')
              : null
          _previewPatchAssistantMessage(root, assistantId, {
            text: template ? template.replace('{message}', msg) : msg,
            streaming: false
          })
          if (root?.update) root.update({ chatTyping: false, currentAssistantId: null })
        },
        // The preview surface runs no client (browser) platform tools — project
        // edits run server-side via commit_project_changes. Answer gracefully so
        // a loop that asks for a client tool can continue with text instead of
        // hanging.
        onToolCall: () => ({
          ok: false,
          error: 'Client tools are not available in the preview surface.'
        })
      }
    )
  }
}

// el.call('bootPreviewAssistant') — one-shot boot from the main page onCreate.
// Resolves the sdk.ai service (singleton getSDK fallback — no el.sdk needed)
// and, on preview STANDALONE only, installs the preview's own transport
// (which flips bridgeStatus → 'open' so the composer enables). Idempotent:
// resolveAiService + installPreviewAiTransport self-guard.
//
// The project the assistant edits is NOT resolved here any more. Inside the
// shell the site on screen is resolved PER SEND from the visible route or
// pane (functions/previewSite.js previewSiteInView) — the one-shot read of
// the ADDRESS BAR that stood here named the wrong site in a pane and kept
// naming a closed one. The public preview host holds no session
// (PREVIEW-HOST-PUBLIC-ONLY, Nika 2026-10-09), so it has no editable project
// to resolve and shows no AI toggle (previewAiAvailable).
export const bootPreviewAssistant = async function () {
  if (typeof window === 'undefined') return
  const el = this
  try {
    const { resolveAiService } = await import('@symbo-ls/workspace-shared/functions/aiService.js')
    await resolveAiService()
  } catch (err) {
    // Anonymous / no-SDK boot — install anyway; sendPrompt surfaces a clear
    // "sign in to edit" / "AI not ready" message rather than failing silently.
    console.warn('[preview] resolveAiService:', err)
  }
  // ONE SHELL ASSISTANT (Nika, 2026-10-03): inside the shell the shell's
  // transport owns the bridge on every page — preview's own sender is for
  // preview STANDALONE only. The shell is detected through the element's own
  // context (the shell registers shellSendPrompt), not a DOM/window probe.
  const inShell = !!(
    el &&
    el.context &&
    el.context.functions &&
    typeof el.context.functions.shellSendPrompt === 'function'
  )
  if (!inShell) installPreviewAiTransport(el)
}

// el.call('reloadPreviewProject') — re-fetch the previewed project for the
// current env and re-render the iframe IF the data changed. Called after each
// assistant turn so committed edits show without a manual refresh.
//
// `opts.force` (STUDIO/FILES-MICRO-1, PreviewNavbar's RefreshBtn): the
// structural-signature short-circuit below exists to skip the expensive
// iframe teardown after a pure-Q&A assistant turn that didn't touch the
// project — but that same short-circuit would make an explicit user click on
// a "Refresh" button silently do nothing whenever the published data hasn't
// changed since the last load, which reads as a broken button, not a no-op
// optimization. `force: true` bypasses the signature check so a manual
// refresh always re-fetches AND always re-mounts the iframe.
// `root._previewRefreshing` is toggled around the whole fetch+mount so the
// toolbar button can show an in-flight state (same convention EnvSwitcher's
// `isLoadingProject` already uses).
export const reloadPreviewProject = async function (opts = {}) {
  const root = this.state && this.state.root
  if (!root) return
  const force = !!(opts && opts.force)
  // SPA-RELOAD-1: swallowing failures is right for the automatic post-turn
  // reload, and wrong for an explicit user-invoked retry — the caller cannot
  // tell a recovered project from a still-broken one. `throwOnError` lets
  // `retryPreviewProjectLoad` re-raise and re-render the error boundary with
  // the CURRENT error instead of leaving the stale boot one on screen.
  const throwOnError = !!(opts && opts.throwOnError)
  // THE SITE BUILDER (the shell's /preview, functions/previewSite.js): the
  // site ON SCREEN — never the address bar's project (a pane's site is not in
  // it) — at its LATEST SAVED version, never an environment's cached
  // published pin. A version pin on the builder's own address (Version
  // History "Preview this version") holds on Refresh; `opts.latest` (an AI
  // write landed) drops it. Preview standalone keeps its address-bar read.
  const fns = (this.context && this.context.functions) || {}
  const builder =
    typeof fns.shellSendPrompt === 'function' && typeof fns.previewSiteFetch === 'function'
  if (force) root.update({ _previewRefreshing: true })
  try {
    let data = null
    let sitePatch = null
    if (builder) {
      const shown =
        this.call('previewSiteInView', root) ||
        (root._previewSite && root._previewSite.owner ? root._previewSite : null) ||
        this.call('previewPageTarget', root)
      if (shown && shown.owner && shown.key) {
        const pin = opts && opts.latest ? null : this.call('previewPageTarget', root)
        const version =
          pin && pin.owner === shown.owner && pin.key === shown.key ? pin.version : undefined
        const got = await this.call('previewSiteFetch', {
          owner: shown.owner,
          key: shown.key,
          version
        })
        data = got && got.data
        const prev = root._previewSite || {}
        const same = prev.owner === shown.owner && prev.key === shown.key
        sitePatch = {
          env: null,
          _previewSiteDraft: !!(got && got.draft),
          _previewSite: {
            owner: shown.owner,
            key: shown.key,
            projectId: (got && got.projectId) || (same && prev.projectId) || null,
            env: null,
            page: (same && prev.page) || '/'
          }
        }
      }
    } else {
      const { getProjectData } =
        await import('@symbo-ls/workspace-shared/functions/fetchProject.js')
      const env = root.env
      data = await getProjectData(env ? { env, raw: true } : { raw: true })
    }
    if (!data) {
      if (throwOnError) throw new Error('Project data unavailable')
      return
    }
    // Cheap structural signature — skip the (expensive) iframe teardown when a
    // pure-Q&A turn didn't actually change the project. A forced (manual
    // button) reload always proceeds regardless.
    let sig = ''
    try {
      sig = JSON.stringify({
        c: data.components,
        p: data.pages,
        a: data.app,
        d: data.designSystem
      })
    } catch (err) {
      console.warn('[preview] sign project data:', err)
    }
    if (sitePatch) root.update(sitePatch)
    if (!force && sig && root._previewDataSig && root._previewDataSig === sig) return
    root.update({ data, _previewDataSig: sig })

    const iframe =
      typeof document !== 'undefined' ? document.getElementById('preview-app-iframe') : null
    if (!iframe) return
    let snapshot = data
    try {
      snapshot = JSON.parse(JSON.stringify(data))
    } catch (err) {
      console.warn('[preview] snapshot project data:', err)
    }
    // A live user-code frame is REMOUNTED over its port with the new data.
    await this.call('renderProject', iframe, snapshot, { reset: true })
  } catch (err) {
    if (throwOnError) throw err
    console.warn('[preview] reloadPreviewProject failed:', err)
  } finally {
    if (force) root.update({ _previewRefreshing: false })
  }
}

// el.call('newSession') — Chat's "New chat" button. Preview keeps sessions
// in-memory only (no server thread list wired), so this just clears the buffer
// and drops the active thread id; the next send starts a fresh server thread.
export const newSession = function () {
  const root = this.state && this.state.root
  if (!root?.update) return
  root.update({
    chatMessages: [],
    currentAssistantId: null,
    activeSessionId: null,
    chatTyping: false,
    showSessionMenu: false
  })
}

// el.call('setModelMode', mode) — persist the model-mode pick to the AI service
// so the next dispatch carries it (the Chat dropdown also writes localStorage).
export const setModelMode = function (mode) {
  try {
    const ai = previewGetAiService()
    if (ai && typeof ai.setModelMode === 'function') ai.setModelMode(mode)
  } catch (err) {
    console.warn('[preview] setModelMode:', err)
  }
}

// el.call('fetchAiProviderDiagnostics') is shared's own registration
// (shared/functions/aiProviderDiagnostics.js). shared is a declared library
// of preview, so its functions bag already answers the key here; a preview
// delegate under the same key was a second definition of one name in a
// catalog that merges both libraries.
